import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';

import { OrderflowBg } from '../../shared/orderflow-bg/orderflow-bg';

import { AuthActions } from '../../state/auth/auth.actions';
import { authFeature, AUTH_STATUS } from '../../state/auth/auth.feature';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, OrderflowBg],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  private readonly store = inject(Store);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);

  readonly form = new FormGroup({
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(6)] }),
  });

  private readonly authStatus = this.store.selectSignal(authFeature.selectStatus);
  private readonly authError = this.store.selectSignal(authFeature.selectError);

  readonly canSubmit = computed(() => !this.loading() && this.form.valid);

  constructor() {
    effect(() => {
      const status = this.authStatus();

      if (status === AUTH_STATUS.loading) {
        this.loading.set(true);
        return;
      }

      this.loading.set(false);

      if (status === AUTH_STATUS.authenticated) {
        this.error.set(null);
        this.success.set('Anmeldung erfolgreich.');
      }

      if (status === AUTH_STATUS.error) {
        this.success.set(null);
        this.error.set(this.authError() ?? 'Anmeldung fehlgeschlagen.');
      }
    });
  }

  onSubmit(): void {
    this.error.set(null);
    this.success.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const email = this.form.controls.email.value.trim();
    const password = this.form.controls.password.value;

    this.store.dispatch(AuthActions.loginRequested({ email, password }));
  }
}
